// 案例數據
export const caseStudies = [
  {
    id: "poc-custom-dev",
    category: "integration",
    title: "Proof of Concept (POC) and Customized Development",
    client: "Multiple Enterprise Clients",
    summary:
      "We transform business concepts into market-ready AI solutions through rapid prototyping and seamless hardware-software integration. Our agile development approach validates your innovation in weeks, not months, while building scalable architecture that supports immediate deployment and future growth.",
    description:
      "Our Proof of Concept (POC) and custom development services deliver tailored smart system solutions that address real-world challenges. We combine cutting-edge AI algorithms with comprehensive hardware-software integration to create application solutions that drive measurable business impact. Through modular development and direct client collaboration, we rapidly build prototypes for functionality validation and optimization. Each solution undergoes rigorous on-site testing and performance tuning. Our scalable system architecture supports seamless data integration and feature expansion, enabling smooth transition from prototype to full production deployment. We manage the complete development lifecycle—from requirement analysis and system design to model training, testing, and deployment—helping clients transform innovative concepts into practical, revenue-generating smart applications with reduced time-to-market and minimized development risk.",
    tags: ["Custom Development", "AI Algorithms", "Hardware-Software Integration"],
    achievements: [
      {
        label: "Direct client collaboration enables rapid prototyping and accelerated concept validation",
        value: "Rapid Prototyping Delivery",
      },
      {
        label: "Small-sample training and simulation testing deliver proven algorithm accuracy and performance",
        value: "Accelerated AI Model Adoption",
      },
      {
        label: "Seamless integration of data collection, logic processing, and device control modules",
        value: "Cross-Module Integration Completed",
      },
      {
        label: "Real-world scenario testing with iterative feature and UI/UX optimization based on user feedback",
        value: "On-Site Validation and Optimization",
      },
      {
        label: "Future-ready APIs and data integration interfaces support productization and large-scale deployment",
        value: "Scalable Application Architecture",
      },
      {
        label: "Continuous on-site operation with ongoing maintenance and updates ensuring system stability and reliability",
        value: "Stable System Deployment",
      },
      {
        label: "Transform innovative concepts into practical, revenue-generating smart application solutions",
        value: "Innovation to Implementation Success",
      },
    ],
    clients: [
      "Creative Biosciences — VitaPCR Data Transmission System",
      "Kaohsiung Medical University — Public Health Bureau Web Crawler Development",
      "Kaohsiung Medical University Innovation Team — Heart Rate Monitoring Wristband App",
      "Lirio Biotechnology — Ultrasound Needle Guide",
    ],
    details: {
      challenges: [
        "Complex application scenarios with requirements that are difficult to clearly define",
        "Traditional development models cannot rapidly validate concept feasibility",
        "Systems require simultaneous integration of hardware, firmware, and algorithm design",
        "Lack of experienced technical integration partners in the market",
        "Inability to adjust functions and interfaces in real time based on field conditions",
        "Absence of long-term maintenance and continuous optimization capabilities",
      ],
      highlights: [
        "Modular development architecture enabling rapid prototyping and iteration",
        "Seamless integration of AI algorithms with hardware devices and systems",
        "Future-ready expansion interfaces supporting productization and scaling",
        "Comprehensive on-site validation mechanisms ensuring practical system deployment",
      ],
    },
    featured: true,
  },
  {
    id: "icu-aiot-system",
    category: "medical",
    title: "ICU Sound Detection AIoT System",
    client: "Chung Shan Medical University Hospital",
    summary:
      "Our ICU Sound Detection AIoT System delivers 85% faster alarm response times by intelligently monitoring medical equipment 24/7. Critical alerts automatically trigger nurse notifications and visual warnings, ensuring immediate response even when staff are attending other patients. This transforms patient safety outcomes while significantly reducing nursing workload in intensive care environments.",
    description:
      "Our customized AIoT alarm assistance system revolutionizes Intensive Care Unit (ICU) operations through advanced sound AI technology. The system accurately detects abnormal alerts from multiple medical devices and instantly triggers nurse call systems with visual indicators, creating a comprehensive alarm response mechanism. Designed to address critical ICU challenges including high nurse-to-patient ratios, numerous medical devices, and complex noisy environments, our solution enables intelligent monitoring across multiple beds and locations simultaneously. Medical staff receive immediate notifications of anomalies regardless of their location, ensuring rapid intervention capabilities. Built for continuous 24/7 operation, the system integrates seamlessly with existing nurse stations and call systems, dramatically improving alarm accuracy and response times. This comprehensive solution reduces nursing stress, minimizes medical risks, and enhances patient safety and care quality, enabling ICUs to achieve more efficient and intelligent operational workflows.",
    tags: ["AI Speech Recognition", "IoT Applications", "Smart Healthcare", "Real-Time Monitoring"],
    achievements: [
      {
        label: "Response time reduced from average 5-8 minutes to under 1 minute",
        value: "85% faster alarm response",
      },
      { label: "Missed alarms decreased from approximately 20% to below 1%", value: "95% reduction in missed alarms" },
      {
        label: "Optimized workforce allocation through reduced unnecessary rounds",
        value: "30% improvement in nursing efficiency",
      },
      {
        label: "Real-time alarm mechanism ensures immediate intervention during emergencies",
        value: "Enhanced emergency response capability",
      },
      {
        label: "Staff receive alerts whether in patient rooms, hallways, or nurse stations",
        value: "Complete multi-location coverage",
      },
      {
        label: "AI system provides continuous intelligent monitoring without interruption",
        value: "24/7 uninterrupted surveillance",
      },
      {
        label: "Significantly reduced risk of medical disputes caused by delayed responses",
        value: "Minimized medical liability risk",
      },
      {
        label: "Substantial reduction in nursing workload and psychological stress",
        value: "Improved nurse job satisfaction",
      },
      {
        label: "Intelligent alarm system optimizes overall ward management efficiency",
        value: "Enhanced resource allocation",
      },
    ],
    clients: ["Chung Shan Medical University Hospital — Sub-Acute Respiratory Care Ward Alarm System"],
    details: {
      challenges: [
        "ICU environments feature high noise levels with multiple medical devices operating simultaneously",
        "High nurse-to-patient ratios require extended bedside care from nursing staff",
        "Traditional central monitoring alarm systems suffer from significant response delays",
        "Nurses cannot immediately detect abnormal alarms from other patient beds or rooms",
      ],
      highlights: [
        "Advanced AI sound recognition algorithms accurately identify medical device alarms in complex noise environments",
        "Comprehensive multi-location coverage system ensures complete area monitoring without blind spots",
        "Instant alert mechanisms support multiple notification methods and communication channels",
        "Measurable reduction in medical risk while substantially improving nursing job satisfaction",
      ],
    },
    featured: true,
  },
  {
    id: "anxin-ecg-system",
    category: "medical",
    title: "AssureM4ORE Pre-Hospital ECG System",
    client: "Pingtung County Fire Department",
    summary:
      "AssureM4ORE revolutionizes emergency cardiac care by enabling real-time ECG transmission from ambulances. Remote physician interpretation ensures patients reach catheterization-capable hospitals directly, eliminating secondary transfers and securing the critical 90-minute treatment window. Seamless HIS/PACS integration allows hospitals to prepare resources in advance, dramatically improving emergency response efficiency and patient survival rates.",
    description:
      "AssureM4ORE transforms acute myocardial infarction (STEMI) emergency care by advancing ECG examinations to the pre-hospital setting. Through real-time data transmission and remote physician interpretation, paramedics complete preliminary diagnoses and treatment decisions at the earliest possible stage. Based on interpretation results, patients are directed to hospitals equipped with catheterization capabilities, significantly reducing unnecessary transfers and critical time delays. The system seamlessly integrates mobile devices with hospital HIS/PACS systems, ensuring instant information transmission and enabling medical teams to prepare resources before patient arrival. By digitalizing and streamlining emergency workflows, AssureM4ORE effectively secures the golden treatment window for STEMI patients, improving survival rates and overall healthcare quality while serving as a transformative solution for emergency and hospital system collaboration.",
    tags: ["Emergency Medicine", "Real-Time Transmission", "Remote Diagnosis", "System Integration"],
    achievements: [
      { label: "Preliminary diagnosis and treatment decisions completed at the emergency scene", value: "Over 70% improvement in emergency decision-making efficiency" },
      {
        label: "Medical teams fully prepared and resources allocated before patient arrival",
        value: "10-20 minute advancement in medical activation time",
      },
      {
        label: "Direct transfer to catheterization-capable hospitals, eliminating secondary transfers",
        value: "Dramatically reduced transfer rates",
      },
      { label: "Seamless real-time communication between paramedics and backend medical teams", value: "Continuous real-time information transmission" },
      {
        label: "Real-time interpretation with standardized data transmission significantly improves diagnostic accuracy",
        value: "Enhanced diagnostic precision",
      },
      { label: "Immediate treatment upon arrival reduces patient waiting time and anxiety", value: "Substantially improved patient experience" },
    ],
    clients: ["Pingtung County Fire Department (implemented in collaboration with local healthcare network)"],
    details: {
      challenges: [
        "Current emergency workflows struggle to achieve vascular reperfusion treatment within the critical 90-minute golden window",
        "Patients frequently sent to hospitals without catheterization facilities, requiring time-consuming secondary transfers",
        "Cannot confirm STEMI cases in real time during ambulance transport",
        "Hospitals unable to pre-allocate resources, causing delays in catheterization lab activation",
      ],
      highlights: [
        "Real-time ECG image upload and remote interpretation platform",
        "Seamless integration with hospital HIS/PACS systems for complete medical record consolidation",
        "Synchronized operation across mobile devices and server systems",
        "Significantly reduced misdiagnosis risk with improved diagnostic accuracy",
      ],
    },
    featured: true,
  },
  {
    id: "jpr-ecg-management",
    category: "medical",
    title: "JPR - End-to-End ECG Digital Management System",
    client: "Pingtung Christian Hospital",
    summary:
      "JPR delivers complete ECG digital transformation by integrating images from ER, outpatient, and health check departments with automatic exam order matching. This streamlines report generation and insurance claim workflows while seamlessly connecting with PACS and HIS systems. The result: enhanced hospital operational efficiency, guaranteed data consistency, and a comprehensive ECG digitalization infrastructure.",
    description:
      "JPR completely digitalizes the ECG workflow from image acquisition and automatic matching to viewing, querying, and report generation. The system integrates seamlessly with existing hospital PACS and HIS systems, providing unified management of ECG images across ER, outpatient, and health check departments, preventing data fragmentation and order mismatches. Through automated matching and image integration capabilities, JPR reduces manual workload, accelerates report generation efficiency, and optimizes data retrieval processes. The platform features automated uploading and insurance claim submission mechanisms, simplifying administrative processes while reducing error rates. Additionally, built-in access control and backup mechanisms strengthen data security and consistency. By improving both healthcare quality and operational efficiency, JPR establishes critical infrastructure for advancing digital transformation in medical information management systems.",
    tags: ["PACS Integration", "Automated Workflow", "Digital Transformation", "Healthcare IT"],
    achievements: [
      {
        label: "Automatic QC matching functionality dramatically reduces manual processing workload",
        value: "85% improvement in image processing efficiency",
      },
      {
        label: "Automated image merging and uploading significantly enhances operational productivity",
        value: "60% reduction in report preparation time",
      },
      {
        label: "Unified viewing platform enables rapid cross-departmental data access and retrieval",
        value: "90% improvement in query response speed",
      },
    ],
    clients: ["Pingtung Christian Hospital"],
    details: {
      challenges: [
        "ECG image sources scattered across ER, general examination, and health check departments",
        "Manual matching processes are extremely time-consuming and prone to human error",
        "Absence of unified viewing platform makes cross-departmental data retrieval difficult",
        "Complex report preparation workflows reduce image integration efficiency",
        "Insufficient integration with existing PACS and HIS systems",
        "Cumbersome insurance claim submission processes increase administrative burden",
      ],
      highlights: [
        "Comprehensive one-stop service with complete digital workflow from image acquisition to report completion",
        "Cross-departmental integration enabling ER, outpatient, and health check units to share a unified platform",
        "Seamless PACS and HIS integration ensuring complete data consistency and reliability",
        "Advanced backup and access control mechanisms providing enhanced data security",
      ],
    },
    featured: false,
  },
  {
    id: "vital-signs-integration",
    category: "medical",
    title: "Vital Signs Monitor Integration System",
    client: "Ministry of Health and Welfare Hospital Network",
    summary:
      "Our three-step workflow—Scan, Measure, Confirm—transforms nursing efficiency by automatically uploading vital signs to the Nursing Information System (NIS). This system delivers 70% improvement in work efficiency, eliminates manual data entry errors, and serves as a cornerstone technology for smart ward transformation across healthcare institutions.",
    description:
      "Our Vital Signs Monitor Integration System revolutionizes nursing operations through a simplified three-step process—Scan, Measure, Confirm—enabling hospitals to integrate patients' vital signs (blood pressure, heart rate, temperature, oxygen saturation) directly into the Nursing Information System (NIS) in real time. This eliminates traditional manual recording and data entry processes, reducing human error while saving significant time and dramatically improving nursing efficiency. In actual hospital deployments, each nurse saves nearly one hour daily on data entry tasks, while clinical data becomes more timely, complete, and accurate. The system integrates seamlessly with existing monitors without requiring hardware replacement, enabling hospitals to achieve smart ward upgrades with minimal capital investment. By enhancing care quality and patient safety outcomes, this solution plays a critical role in optimizing nursing workflows and realizing comprehensive smart healthcare transformation.",
    tags: ["Nursing Information", "Automated Upload", "Workflow Optimization", "Data Integration"],
    achievements: [
      {
        label: "Measurement time reduced from 5 minutes to 1.5 minutes, saving nurses 1 hour of data entry daily",
        value: "70% improvement in work efficiency",
      },
      {
        label: "Eliminated human error rates with real-time uploads minimizing data loss risks",
        value: "Significantly improved data accuracy",
      },
      {
        label: "Successfully integrated monitors with nursing systems, supporting automatic capture of multiple vital parameters",
        value: "Complete system integration achieved",
      },
      { label: "Streamlined three-step workflow: Scan → Measure → Upload", value: "Dramatically simplified process" },
    ],
    clients: ["MOHW Xinying Hospital", "MOHW Taichung Hospital"],
    details: {
      challenges: [
        "Traditional ward workflows for vital sign measurement are complex and extremely time-consuming",
        "Nurses must manually record vital signs and enter data individually into the NIS system",
        "Labor-intensive processes are highly prone to human error and data inconsistencies",
        "Hospitals face increasing operational pressure from critical nursing staff shortages",
      ],
      highlights: [
        "Intuitive three-step workflow: scan patient wristband → measure vitals → confirm upload",
        "Successfully integrated existing monitoring devices with Nursing Information Systems",
        "Supports automatic capture and processing of multiple vital parameters simultaneously",
        "Real-time data transmission ensures complete data integrity and system reliability",
      ],
    },
    featured: false,
  },
  {
    id: "physio-measurement-system",
    category: "medical",
    title: "Multi-Device Physiological Measurement System",
    client: "Armed Forces Taoyuan General Hospital, among others",
    summary:
      "Our integrated system connects multiple physiological measurement devices including blood pressure monitors and height-weight scales through intuitive touchscreen operation. Measurement data uploads instantly and synchronizes with medical systems, delivering 60% improvement in operational efficiency and 85% improvement in data accuracy while providing patients with safer, more convenient healthcare experiences.",
    description:
      "This comprehensive system integrates various commonly used physiological measurement devices (blood pressure monitors, height-weight scales, etc.) through an intuitive touchscreen interface supporting both National Health Insurance (NHI) card insertion and manual input methods. Patients and nurses can complete measurements quickly and easily with streamlined workflows. Upon measurement completion, data is stored and uploaded in real time, with sharing capabilities for both patients and healthcare professionals. Our system addresses long-standing challenges in medical settings including independently operated devices, cumbersome workflows, and lack of real-time data integration—issues that previously resulted in heavy workloads and difficulty tracking patient data trends. By simplifying workflows and integrating all measurement data into existing hospital information systems, this solution enhances smart healthcare service levels, reduces human errors and redundant tasks, while providing patients with more seamless and reassuring care experiences.",
    tags: ["Device Integration", "Smart Identity Recognition", "Real-Time Processing", "Multi-Channel Sharing"],
    achievements: [
      {
        label: "Integrated interface reduced average measurement time from 5 minutes to 2 minutes",
        value: "60% improvement in operational efficiency",
      },
      { label: "Automated data transmission eliminated manual transcription errors", value: "85% improvement in data accuracy" },
      { label: "Unified management platform optimized equipment utilization rates", value: "40% increase in device utilization" },
      { label: "Automated workflows reduced repetitive nursing tasks and administrative burden", value: "30% reduction in labor costs" },
    ],
    clients: [
      "Taoyuan Armed Forces General Hospital — Blood Pressure Data Upload System",
      "Dakuixi Community Development Association — Blood Pressure Data Upload System",
      "Heyue Clinic — Blood Pressure Data Upload System",
    ],
    details: {
      challenges: [
        "Device integration difficulties: blood pressure monitors and height-weight scales operate as independent systems",
        "Complex workflows requiring patients and nurses to separately operate different measurement devices",
        "Fragmented data management: measurement results cannot be immediately integrated into hospital information systems",
        "Poor user experience due to lack of intuitive user interfaces and guidance",
        "Data tracking difficulties: long-term patient physiological data trends cannot be effectively monitored or analyzed",
      ],
      highlights: [
        "Comprehensive multi-device integration: successfully unified blood pressure monitors, height-weight scales, and other physiological measurement devices",
        "Smart identity recognition: supports both NHI card insertion for automatic identification and manual ID input methods",
        "Real-time data processing: measurement results are instantly displayed, stored, processed, and transmitted",
        "User-friendly interface: intuitive touchscreen interface designed for users across all age groups and technical skill levels",
      ],
    },
    featured: false,
  },
  {
    id: "ecg-image-conversion",
    category: "medical",
    title: "ECG Image Conversion and Upload System",
    client: "Multiple Medical Institutions",
    summary:
      "Our customized ECG image conversion and upload system enables real-time DICOM format conversion and PACS uploading directly in clinical settings. Supporting multiple device brands with lightweight deployment, the system delivers 70% reduction in workflow time, 100% data conversion accuracy, and 80% faster deployment compared to traditional large-scale systems while allowing medical staff to focus on patient care.",
    description:
      "This tailored solution serves multiple healthcare institutions by handling ECG image conversion and uploading with advanced automation capabilities. The system supports immediate DICOM format conversion, patient exam order matching, and PACS system uploading directly in clinical environments. With lightweight deployment architecture and flexible integration capabilities, it avoids the high costs and complexity associated with large-scale system implementations, enabling frontline medical staff to process and archive ECG data instantly. This dramatically simplifies workflows while enhancing operational efficiency. Our system maintains compatibility with various ECG device brands and integrates seamlessly with existing HIS and PACS platforms, reducing maintenance workload while accelerating healthcare digitalization initiatives. Once deployed, medical staff can focus entirely on patient care while backend data synchronization occurs automatically, improving operational fluency and overall care quality.",
    tags: ["DICOM Conversion", "PACS Integration", "Lightweight Deployment", "Real-Time Processing"],
    achievements: [
      {
        label: "Reduced average ECG post-processing time by 70% through automated workflows",
        value: "70% reduction in workflow time",
      },
      {
        label: "Achieved 100% success rate in DICOM format conversion across all device types",
        value: "100% data conversion accuracy",
      },
      {
        label: "100% compatibility with existing PACS systems without requiring additional hardware investment",
        value: "Complete system integration compatibility",
      },
      {
        label: "Average deployment completed within 1-3 business days, reducing setup time by 80% compared to traditional large systems",
        value: "80% reduction in deployment time",
      },
    ],
    clients: [
      "Taipei Veterans General Hospital Taitung Branch — Image Conversion Software",
      "National Cheng Kung University Hospital Douliu Branch — ECG Upload System",
      "Ping An Hospital — ECG Upload System",
      "Taoyuan Armed Forces Hospital Health Examination Center — ECG Transmission System",
    ],
    details: {
      challenges: [
        "Diverse ECG machine models lacking unified data format conversion mechanisms",
        "Examination data requires manual processing and cannot be integrated into hospital systems in real time",
        "PACS (Picture Archiving and Communication System) upload workflows are overly complex and time-consuming",
        "Clinical workflows are inefficient, significantly affecting staff productivity and patient throughput",
        "Lack of flexible system integration solutions combined with prohibitively high costs for large-scale system implementation",
      ],
      highlights: [
        "Lightweight solution architecture delivering comprehensive services without requiring large-scale system implementation",
        "Universal support for data format conversion across multiple ECG machine brands and models",
        "Real-time DICOM conversion and PACS uploading functionality with automated quality control",
        "Rapid deployment capabilities significantly reducing system implementation costs and timeframes",
      ],
    },
    featured: false,
  },
  {
    id: "jaundice-digital-system",
    category: "medical",
    title: "Jaundice Analyzer Digital Integration System",
    client: "Dozens of Hospitals Across Taiwan",
    summary:
      "Operating successfully since 2010 across dozens of hospitals in Taiwan, our system enables automated uploading of jaundice analyzer data into hospital information systems. By eliminating manual transcription processes, it delivers 80% improvement in testing efficiency, reduces errors by over 95%, and has provided reliable service for 13+ years, earning outstanding recognition from healthcare professionals nationwide.",
    description:
      "Designed specifically for jaundice analyzer digital integration, this proven system utilizes customized connectivity software to automatically upload test data to hospital information systems. Since 2010, it has operated with exceptional stability across dozens of medical institutions throughout Taiwan, helping clinical units eliminate manual transcription and data entry requirements. This has dramatically enhanced testing efficiency while reducing human error risks to negligible levels. With high compatibility standards, the system can be customized to different hospital workflows and integrated seamlessly with HIS/LIS platforms, supporting establishment of standardized processes while strengthening data management capabilities. Its 13-year track record of stable operation demonstrates exceptional reliability and minimal maintenance costs, making it a trusted and practical solution for instrument data integration across medical institutions of all sizes.",
    tags: ["Digital Transformation", "Automated Upload", "Long-Term Operation", "Standardized Workflow"],
    achievements: [
      {
        label: "Successfully deployed across dozens of hospitals in Taiwan with over 13 years of stable operation since 2010",
        value: "Extensive proven service coverage",
      },
      {
        label: "Reduced test data processing time by 80% while lowering manual error rates by over 95%",
        value: "Dramatic efficiency and accuracy improvements",
      },
      {
        label: "Delivered 15-20% annual labor cost savings per hospital with minimal ongoing maintenance expenses",
        value: "Exceptional cost-effectiveness and ROI",
      },
      {
        label: "Custom SOPs improved workflow consistency while achieving seamless integration with existing HIS/LIS systems",
        value: "Optimized process standardization",
      },
      {
        label: "Ensured complete data integrity and accuracy with zero major system failures throughout long-term operation",
        value: "Guaranteed data quality assurance",
      },
      {
        label: "Consistently high praise from medical staff for exceptional ease of use and system reliability",
        value: "Outstanding user satisfaction ratings",
      },
    ],
    clients: [
      "National Taiwan University Hospital System (Main Hospital, Children's Hospital, Hsinchu, Zhubei Biomedical, Zhudong Biomedical, Huwei, Douliu)",
      "Kaohsiung Medical University Hospital System (Main Hospital, Gangshan Branch, Yanchao Datong)",
      "Veterans General Hospital System (Taipei, Taichung)",
      "Chi Mei Medical Center System (Yongkang, Liouying)",
      "Tzu Chi Hospital System (Taichung, Hualien, Dalin, Chiayi, Kaohsiung)",
      "Chang Gung Memorial Hospital System (Keelung, Taipei, Taoyuan)",
      "Taipei City Hospital",
      "Ministry of Health and Welfare Tucheng Hospital",
      "Yuan's General Hospital",
      "Pingtung Christian Hospital",
      "National Cheng Kung University Hospital",
      "Far Eastern Memorial Hospital",
    ],
    details: {
      challenges: [
        "Data silos: Analyzer-generated data cannot be directly integrated into hospital information systems",
        "Manual workload burden: Laboratory staff must manually record and enter data, creating time-consuming and error-prone processes",
        "Standardization requirements: Different hospitals require customized workflows for laboratory and clinical departments",
        "Data management efficiency: Lack of unified data formats and automated upload mechanisms across institutions",
      ],
      highlights: [
        "Over 13 years of continuous stable operation with proven technical reliability and system maturity",
        "Successfully deployed across dozens of Taiwan's most renowned hospitals, covering major medical systems nationwide",
        "Automated data capture and upload capabilities significantly reducing human errors and processing time",
        "Custom Standard Operating Procedure (SOP) development tailored to meet varying hospital requirements and workflows",
      ],
    },
    featured: true,
  },
  {
    id: "restroom-smart-sensor",
    category: "iot",
    title: "Smart Restroom Sensing System",
    client: "Kaohsiung Municipal Siaogang Hospital",
    summary:
      "Combining magnetic reed sensors and infrared motion detection with intelligent algorithms, our system monitors user activity in real time. When abnormalities occur (such as prolonged inactivity), automated alerts enable immediate staff response. This enhances restroom safety for patients and families while improving overall healthcare quality and emergency response capabilities.",
    description:
      "Our Smart Restroom Sensing System integrates magnetic reed sensors with infrared motion detection technology to monitor user activity in restrooms continuously. Through advanced logic-based algorithms, the system identifies potential abnormalities such as extended periods of inactivity or distress situations. Upon detecting potential emergencies, the system automatically triggers alert notifications, enabling medical staff to respond quickly and effectively to safeguard patients who use restroom facilities independently. This comprehensive safety solution addresses critical blind spots in patient monitoring while maintaining privacy and dignity.",
    tags: ["IoT Sensing", "Intelligent Computing", "Safety Monitoring", "Real-Time Alerts"],
    achievements: [
      {
        label: "Real-time detection of restroom activity with automatic staff alerts when no movement is detected for extended periods",
        value: "Comprehensive safety monitoring and alert system",
      },
      {
        label: "Integration of magnetic reed switches and PIR sensors with multi-layered logic algorithms reduces false alarms and enhances detection accuracy",
        value: "Advanced intelligent detection capabilities",
      },
      {
        label: "Successfully enabled early detection of abnormal events with timely staff interventions, establishing an effective restroom safety monitoring model",
        value: "Proven implementation with measurable safety outcomes",
      },
      {
        label: "System automatically switches to monitoring logic mode when door closure is detected and no motion is sensed",
        value: "Intelligent automatic mode switching",
      },
    ],
    clients: ["Kaohsiung Municipal Siaogang Hospital"],
    details: {
      challenges: [
        "Patients visiting healthcare facilities alone may be unable to call for help during medical emergencies (falls, fainting) occurring in restrooms",
        "High risk of missing the critical golden rescue window for emergency medical intervention",
        "Public restroom environments discourage unnecessary disturbance, making abnormal events difficult to detect promptly",
        "Significant patient safety risks and monitoring blind spots are created in unsupervised restroom areas",
      ],
      highlights: [
        "Dual-technology integration combining magnetic reed sensing and infrared motion detection for comprehensive coverage",
        "Intelligent algorithms with sophisticated logic-based processing significantly improve detection accuracy while minimizing false positives",
        "Fully automated alert mechanism requiring no manual intervention or constant staff monitoring",
        "Multi-layered detection logic effectively reduces both false alarms and missed detection incidents",
      ],
    },
    featured: false,
  },
];

// Category Information
export const categories = [
  { id: "all", name: "All Cases", count: caseStudies.length },
  {
    id: "medical",
    name: "Medical",
    count: caseStudies.filter((c) => c.category === "medical").length,
  },
  { id: "sports", name: "Sports", count: 0 }, // No sports cases currently
  { id: "bi", name: "Business Intelligence", count: 0 }, // No BI cases currently
  {
    id: "iot",
    name: "IoT",
    count: caseStudies.filter((c) => c.category === "iot").length,
  },
  {
    id: "integration",
    name: "System Integration",
    count: caseStudies.filter((c) => c.category === "integration").length,
  },
];